import { useQuery } from "@tanstack/react-query";
import {
  GET_PARAGON_CONFIG,
  getParagonConfigs,
} from "@/api/client/paragon-config";
import {
  ParagonPipeline,
  ParagonSyncRecord,
  ParagonSyncStatus,
} from "@/types/paragon-config";

interface ParagonSyncStatusResult {
  isContactSyncInProgress: boolean;
  isLoading: boolean;
  activeSyncs: ParagonSyncRecord[];
}

export const useParagonSyncStatus = (): ParagonSyncStatusResult => {
  const { data: paragonConfigs, isLoading } = useQuery({
    queryKey: [GET_PARAGON_CONFIG, "sync-status"],
    queryFn: () => getParagonConfigs(),
    staleTime: 5000, // Data is fresh for 5s, prevents redundant refetches
    refetchInterval: (query) => {
      // Poll every 5 seconds if sync is in progress
      const configs = query.state.data;
      const hasActiveSync = configs?.some((config) =>
        config.syncs?.some((sync) => {
          const status = (sync.status as string)?.toUpperCase();
          const pipeline = (sync.pipeline as string)?.toLowerCase();
          return (
            pipeline === ParagonPipeline.CONTACTS &&
            status === ParagonSyncStatus.INITIALIZING
          );
        }),
      );
      return hasActiveSync ? 5000 : false;
    },
    // Re-check on focus so a sync started elsewhere (e.g. a firm admin changing
    // the CRM sync object in firm-admin) is detected when the user returns to
    // this tab, even if no sync was active when the tab lost focus.
    refetchOnWindowFocus: true,
  });

  // Find all active contact syncs across all integrations
  const activeSyncs: ParagonSyncRecord[] = [];
  let isContactSyncInProgress = false;

  if (paragonConfigs) {
    for (const config of paragonConfigs) {
      if (config.syncs) {
        for (const sync of config.syncs) {
          const status = (sync.status as string)?.toUpperCase();
          const pipeline = (sync.pipeline as string)?.toLowerCase();
          if (
            pipeline === ParagonPipeline.CONTACTS &&
            status === ParagonSyncStatus.INITIALIZING
          ) {
            isContactSyncInProgress = true;
            activeSyncs.push(sync);
          }
        }
      }
    }
  }

  return {
    isContactSyncInProgress,
    isLoading,
    activeSyncs,
  };
};
